/* Lunarie Select Club — form runtime
 * - persistent UTM / click-id attribution
 * - Meta browser+server event deduplication (shared event_id)
 * - conditional questions, inline validation, bot traps
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Attribution: capture once, keep for 30 days, reuse everywhere.
   * ------------------------------------------------------------------ */
  var ATTR_KEY = 'lsc_attribution';
  var ATTR_TTL = 30 * 24 * 60 * 60 * 1000;
  var TRACKED = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'sccid', 'ref'
  ];

  function readStore(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeStore(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* private mode */ }
  }
  function cookie(name) {
    var m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[2]) : '';
  }

  function attribution() {
    var stored = readStore(ATTR_KEY);
    if (stored && Date.now() - stored.captured_at > ATTR_TTL) stored = null;

    var params = new URLSearchParams(location.search);
    var fresh = {};
    TRACKED.forEach(function (k) { if (params.get(k)) fresh[k] = params.get(k); });

    var data;
    if (Object.keys(fresh).length) {
      // new campaign click wins, but keep the original first-touch
      data = Object.assign({}, fresh, {
        captured_at: Date.now(),
        landing_page: location.href.split('#')[0],
        referrer: document.referrer || '',
        first_touch: (stored && stored.first_touch) || Object.assign({ at: Date.now() }, fresh)
      });
      writeStore(ATTR_KEY, data);
    } else if (stored) {
      data = stored;
    } else {
      data = {
        captured_at: Date.now(),
        landing_page: location.href.split('#')[0],
        referrer: document.referrer || ''
      };
      writeStore(ATTR_KEY, data);
    }

    // Meta browser identifiers. _fbc is only set by the pixel; synthesize it
    // from fbclid so server events can still be click-attributed.
    var fbc = cookie('_fbc');
    if (!fbc && data.fbclid) fbc = 'fb.1.' + (data.captured_at || Date.now()) + '.' + data.fbclid;

    return Object.assign({}, data, { fbp: cookie('_fbp') || '', fbc: fbc || '' });
  }

  window.LunarieAttribution = attribution;

  // Capture on page load, not only at submit. attribution() reads the UTMs
  // from the current URL, so when it ran only at submit, a visitor who landed
  // from an ad, browsed a product and came back submitted from a clean URL
  // with nothing stored - and was recorded as direct.
  try { attribution(); } catch (e) { /* storage blocked: submit still reads the URL */ }

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  /**
   * A stable first-party visitor ID, shared with the Shopify web pixel through
   * the same cookie. Sent as external_id on every event so Meta can connect an
   * anonymous browsing session to the person who later fills in the form.
   */
  function visitorId() {
    var NAME = '_lsc_uid';
    var existing = cookie(NAME);
    if (existing) return existing;
    var v = uuid().replace(/-/g, '');
    try {
      document.cookie = NAME + '=' + v + ';path=/;max-age=34560000;SameSite=Lax' +
        (location.protocol === 'https:' ? ';Secure' : '');
    } catch (e) { /* cookies blocked - the id is then per-page, still valid */ }
    return v;
  }

  function setError(scope, name, msg) {
    var el = scope.querySelector('[data-error-for="' + name + '"]');
    if (el) el.textContent = msg || '';
  }

  /* ------------------------------------------------------------------ *
   * Section controller
   * ------------------------------------------------------------------ */
  function init(root) {
    if (root.dataset.lscReady === '1') return;
    root.dataset.lscReady = '1';

    var form = root.querySelector('[data-lsc-form]');
    if (!form) return;

    var submitBtn   = root.querySelector('[data-lsc-submit]');
    var statusEl    = root.querySelector('[data-lsc-status]');
    var formErrorEl = root.querySelector('[data-lsc-formerror]');
    var checking    = root.querySelector('[data-lsc-checking]');
    var stepEl      = root.querySelector('[data-lsc-step]');
    var progressEl  = root.querySelector('[data-lsc-progress]');
    var successEl   = root.querySelector('[data-lsc-success]');
    var successCta  = root.querySelector('[data-lsc-success-cta]');
    var tsField     = root.querySelector('[data-lsc-ts]');
    var questions   = Array.prototype.slice.call(root.querySelectorAll('[data-lsc-question]'));
    var meter       = root.querySelector('[data-lsc-meter]');
    var meterFill   = root.querySelector('[data-lsc-meter-fill]');
    var meterCount  = root.querySelector('[data-lsc-meter-count]');
    var meterTotal  = root.querySelector('[data-lsc-meter-total]');
    var phoneRequired = root.dataset.phoneRequired === 'true';
    // Must agree with the Worker's LEAD_VALUE: this event and the server's
    // share one event_id, so Meta keeps one of the pair and the two must not
    // describe the same lead as worth different amounts.
    var leadValue = parseFloat(root.dataset.leadValue) || 0;

    var endpoint   = root.dataset.endpoint;
    var loaderMs   = parseInt(root.dataset.loaderMs, 10) || 3400;
    var submitting = false;
    var idempotencyKey = uuid();

    if (tsField) tsField.value = String(Date.now());

    /* ---- Turnstile (optional) ---- */
    var turnstileToken = '';
    var turnstileId = null;
    var tsEl = root.querySelector('[data-lsc-turnstile]');
    if (tsEl && root.dataset.turnstileKey) {
      var mount = function () {
        if (!window.turnstile) return setTimeout(mount, 200);
        turnstileId = window.turnstile.render(tsEl, {
          sitekey: root.dataset.turnstileKey,
          callback: function (t) { turnstileToken = t; },
          // Turnstile tokens expire after ~5 minutes. This survey is eight
          // questions plus four contact fields, so a real person can easily
          // still be typing when that happens. Merely clearing the token would
          // fail their FIRST submit - the worst possible place to lose a lead -
          // and only the retry would work. Re-running the challenge instead
          // fetches a fresh token silently, before they ever press the button.
          'expired-callback': function () {
            turnstileToken = '';
            try { window.turnstile.reset(turnstileId); } catch (e) { /* widget gone */ }
          },
          // Network blip or challenge failure: drop the stale token so we never
          // submit one the Worker will reject.
          'error-callback': function () { turnstileToken = ''; }
        });
      };
      mount();
    }

    /* ---- option buttons ---- */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('.lsc__option');
      if (!btn || !root.contains(btn)) return;
      var group = btn.closest('[data-lsc-question]');
      group.querySelectorAll('.lsc__option').forEach(function (b) {
        b.setAttribute('aria-checked', String(b === btn));
      });
      group.querySelector('[data-lsc-answer]').value = btn.dataset.value;
      group.querySelector('.lsc__options').classList.remove('is-invalid');
      setError(group, 'q__' + group.dataset.key, '');
      applyConditionals();
      updateMeter();
    });

    // keyboard support for the radiogroup
    root.addEventListener('keydown', function (e) {
      var btn = e.target.closest('.lsc__option');
      if (!btn) return;
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
      if (keys.indexOf(e.key) === -1) return;
      e.preventDefault();
      var all = Array.prototype.slice.call(btn.closest('.lsc__options').children);
      var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
      var next = all[(all.indexOf(btn) + dir + all.length) % all.length];
      next.focus();
      next.click();
    });

    /* ---- conditional logic ---- */
    function answerFor(key) {
      var q = questions.filter(function (n) { return n.dataset.key === key; })[0];
      return q ? (q.querySelector('[data-lsc-answer]').value || '') : '';
    }
    function updateMeter() {
      var visible = questions.filter(function (q) { return !q.hidden; });
      var answered = visible.filter(function (q) {
        var done = Boolean(q.querySelector('[data-lsc-answer]').value);
        q.classList.toggle('lsc__q--done', done);
        return done;
      });
      if (!meter) return;
      if (answered.length > 0) meter.hidden = false;
      if (meterTotal) meterTotal.textContent = String(visible.length);
      if (meterCount) meterCount.textContent = String(answered.length);
      if (meterFill) meterFill.style.width = visible.length
        ? Math.round((answered.length / visible.length) * 100) + '%'
        : '0%';
    }

    function applyConditionals() {
      questions.forEach(function (q) {
        var key = q.dataset.showIfKey;
        if (!key) return;
        var want = (q.dataset.showIfValue || '').split(',').map(function (s) {
          return s.trim().toLowerCase();
        }).filter(Boolean);
        var have = answerFor(key).trim().toLowerCase();
        var visible = want.length ? want.indexOf(have) !== -1 : Boolean(have);
        q.hidden = !visible;
        if (!visible) q.querySelector('[data-lsc-answer]').value = '';
      });
    }
    applyConditionals();
    updateMeter();

    /* ---- validation ---- */
    var countrySelect = form.querySelector('[data-lsc-country]');
    function dial() {
      if (!countrySelect) return '1';
      var opt = countrySelect.options[countrySelect.selectedIndex];
      return (opt && opt.dataset.dial) || '1';
    }
    function e164() {
      var raw = (form.phone.value || '').replace(/\D/g, '');
      if (!raw) return '';
      var code = dial();
      if (raw.indexOf(code) === 0 && raw.length > code.length + 6) return '+' + raw;
      return '+' + code + raw.replace(/^0+/, '');
    }

    function validate() {
      var errors = [];
      [['first_name', 'Please enter your first name.'],
       ['last_name', 'Please enter your last name.']].forEach(function (pair) {
        var input = form[pair[0]];
        var ok = input.value.trim().length >= 1;
        input.classList.toggle('is-invalid', !ok);
        setError(form, pair[0], ok ? '' : pair[1]);
        if (!ok) errors.push(pair[0]);
      });

      var emailOk = EMAIL_RE.test(form.email.value.trim());
      form.email.classList.toggle('is-invalid', !emailOk);
      setError(form, 'email', emailOk ? '' : 'Please enter a valid email address.');
      if (!emailOk) errors.push('email');

      var digits = (form.phone.value || '').replace(/\D/g, '');
      var phoneOk;
      if (!digits.length && !phoneRequired) {
        phoneOk = true;                       // left blank on purpose — that's allowed
      } else {
        phoneOk = digits.length >= 7 && digits.length <= 15;
      }
      form.phone.classList.toggle('is-invalid', !phoneOk);
      setError(form, 'phone', phoneOk ? '' : (digits.length
        ? 'Please enter a valid phone number.'
        : 'Please enter your phone number.'));
      if (!phoneOk) errors.push('phone');

      questions.forEach(function (q) {
        if (q.hidden || q.dataset.required !== 'true') return;
        var ok = Boolean(q.querySelector('[data-lsc-answer]').value);
        q.querySelector('.lsc__options').classList.toggle('is-invalid', !ok);
        setError(q, 'q__' + q.dataset.key, ok ? '' : 'Please choose an option.');
        if (!ok) errors.push(q.dataset.key);
      });

      // Agreement gates submission outright - it covers the terms of service,
      // not just SMS, so a missing phone number is no excuse to skip it.
      var consent = form.querySelector('[data-lsc-consent]');
      var consentBox = form.querySelector('.lsc__consent');
      if (consent && consent.required && !consent.checked) {
        setError(form, 'sms_consent', 'Please agree to the terms to continue.');
        if (consentBox) consentBox.classList.add('is-invalid');
        errors.push('sms_consent');
      } else {
        setError(form, 'sms_consent', '');
        if (consentBox) consentBox.classList.remove('is-invalid');
      }

      return errors;
    }

    var consentEl = form.querySelector('[data-lsc-consent]');
    if (consentEl) {
      consentEl.addEventListener('change', function () {
        if (!consentEl.checked) return;
        setError(form, 'sms_consent', '');
        var box = form.querySelector('.lsc__consent');
        if (box) box.classList.remove('is-invalid');
      });
    }

    form.addEventListener('input', function (e) {
      if (e.target.classList.contains('is-invalid')) {
        e.target.classList.remove('is-invalid');
        setError(form, e.target.name, '');
      }
    });

    /* ---- payload ---- */
    function collect(eventId) {
      var answers = {};
      questions.forEach(function (q) {
        if (q.hidden) return;
        answers[q.dataset.key] = q.querySelector('[data-lsc-answer]').value || null;
      });
      var consent = form.querySelector('[data-lsc-consent]');
      var consentText = consent ? (consent.closest('label').innerText || '').trim() : '';

      var phone = e164();
      return {
        form_id: root.dataset.formId,
        external_id: visitorId(),
        destination: root.dataset.successUrl || null,
        discount: root.dataset.discount || null,
        event_id: eventId,
        idempotency_key: idempotencyKey,
        shop: root.dataset.shop,
        customer_id: root.dataset.customerId || null,
        contact: {
          first_name: form.first_name.value.trim(),
          last_name: form.last_name.value.trim(),
          email: form.email.value.trim().toLowerCase(),
          phone: phone,
          phone_country: countrySelect ? countrySelect.value : null
        },
        answers: answers,
        consent: {
          sms: Boolean(phone) && Boolean(consent && consent.checked),
          text: consentText.slice(0, 1000),
          collected_at: new Date().toISOString(),
          source_url: location.href.split('#')[0]
        },
        attribution: attribution(),
        page: {
          url: location.href.split('#')[0],
          title: document.title,
          referrer: document.referrer || '',
          screen: window.innerWidth + 'x' + window.innerHeight,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        bot: {
          hp: form.company_website.value,
          elapsed_ms: Date.now() - parseInt(tsField.value, 10)
        },
        turnstile_token: turnstileToken || null
      };
    }

    
    var STEPS = [
      'Validating your answers…',
      'Checking availability in your region…',
      'Matching you with a collection…',
      'Reserving your spot…'
    ];
    function runChecking() {
      checking.hidden = false;
      var per = loaderMs / STEPS.length;
      STEPS.forEach(function (text, i) {
        setTimeout(function () {
          if (checking.hidden) return;
          stepEl.textContent = text;
          progressEl.style.width = Math.round(((i + 1) / STEPS.length) * 96) + '%';
        }, i * per);
      });
      return sleep(loaderMs).then(function () { progressEl.style.width = '100%'; return sleep(220); });
    }

    function showSuccess(result) {
      checking.hidden = true;
      form.hidden = true;
      successEl.hidden = false;
      if (result && result.redirect_url && successCta) successCta.href = result.redirect_url;
      successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

      var url = (successCta && successCta.getAttribute('href')) || '';
      if (url && root.dataset.autoRedirect === 'true') {
        var delay = parseInt(root.dataset.redirectDelay, 10) || 3000;
        var note = root.querySelector('[data-lsc-redirect-note]');
        if (note) note.hidden = false;
        setTimeout(function () { location.href = url; }, delay);
      }
    }

    /* ---- submit ---- */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitting) return;

      formErrorEl.textContent = '';
      var errors = validate();
      if (errors.length) {
        var first = root.querySelector('.is-invalid');
        if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        formErrorEl.textContent = 'Please complete the highlighted fields.';
        return;
      }

      submitting = true;
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      statusEl.textContent = '';

      var eventId = uuid();
      var payload = collect(eventId);

      // Browser-side Lead — same event_id as the server event so Meta dedupes.
      try {
        if (window.fbq) {
          fbq('track', 'Lead', {
            content_name: payload.form_id,
            currency: 'USD',
            value: leadValue
          }, { eventID: eventId });
        }
      } catch (err) { /* pixel absent */ }

      var request = fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Idempotency-Key': idempotencyKey },
        body: JSON.stringify(payload),
        keepalive: true
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) throw Object.assign(new Error(body.message || 'Request failed'), { status: res.status, body: body });
          return body;
        });
      });

      Promise.all([request, runChecking()]).then(function (out) {
        showSuccess(out[0]);
      }).catch(function (err) {
        checking.hidden = true;
        submitting = false;
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
        // NOTE: a repeat submission is NOT an error here - the Worker replays the
        // stored response with 200 + duplicate:true, so it lands in the success
        // path above. There is deliberately no 409 branch; the Worker never
        // sends one, and the dead branch that used to sit here implied otherwise.
        formErrorEl.textContent = (err && err.body && err.body.message) ||
          'Something went wrong on our end. Please try again in a moment.';
        if (window.turnstile) { try { window.turnstile.reset(turnstileId); } catch (e2) {} }
        turnstileToken = '';
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-lsc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-lsc]').forEach(init);
  });
})();